import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import type { SecurityDepositBreakdown } from "../../pages/refund-management/editRefundDepositBreakdown";

function formatBreakdownAmount(amount: number | null | undefined): string {
  const n =
    amount == null || !Number.isFinite(Number(amount)) ? 0 : Number(amount);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const REFUND_PROCESSED_MESSAGE = "Refund has been processed for this record.";

type BreakdownRow =
  | {
      kind: "amount";
      label: string;
      value: string;
    }
  | {
      kind: "message";
      text: string;
      color?: string;
    };

export interface SecurityDepositBreakdownCardProps {
  breakdown: SecurityDepositBreakdown;
  subtitle?: string;
  className?: string;
}

const SecurityDepositBreakdownCard: React.FC<
  SecurityDepositBreakdownCardProps
> = ({
  breakdown,
  subtitle = "Payment and refund summary for this booking",
  className = "mb-4 shadow-sm border-0 rounded-3",
}) => {
  const rows: BreakdownRow[] = [
    {
      kind: "amount",
      label: "Total Amount",
      value: formatBreakdownAmount(breakdown.totalPayment),
    },
    {
      kind: "amount",
      label: "Proposed Refundable Amount",
      value: formatBreakdownAmount(breakdown.proposedRefundAmount),
    },
    {
      kind: "amount",
      label: "Refunded Amount",
      value: formatBreakdownAmount(
        breakdown.isRefundProcessed
          ? breakdown.amountRefunded
          : breakdown.refundableAmount,
      ),
    },
  ];

  if (breakdown.isRefundProcessed) {
    rows.push({
      kind: "message",
      text: REFUND_PROCESSED_MESSAGE,
      color: "success.main",
    });
  }

  return (
    <Paper className={className} sx={{ border: "1px solid #e0e0e0" }}>
      <Box className="px-4 pt-4 pb-4">
        <Typography
          variant="h6"
          className="fw-bold mb-1"
          sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
        >
          Security Deposit Breakdown
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {subtitle}
        </Typography>
        <Box
          sx={{
            borderRadius: 1,
            border: "1px solid #e9ecef",
            bgcolor: "#fafafa",
            overflow: "hidden",
          }}
        >
          {rows.map((row, index) =>
            row.kind === "message" ? (
              <Box
                key={`message-${row.text}`}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom:
                    index < rows.length - 1 ? "1px solid #eee" : "none",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: row.color ?? "success.main",
                    fontWeight: 500,
                  }}
                >
                  {row.text}
                </Typography>
              </Box>
            ) : (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderBottom:
                    index < rows.length - 1 ? "1px solid #eee" : "none",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    textAlign: "right",
                    maxWidth: "60%",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ),
          )}
        </Box>
        {breakdown.breakdownCaption ? (
          <Typography
            variant="caption"
            sx={{
              color: breakdown.breakdownCaption.color,
              display: "block",
              mt: 1.5,
            }}
          >
            {breakdown.breakdownCaption.text}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
};

export default SecurityDepositBreakdownCard;
